import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';
import { characters } from '@/mocks/characters';

const factions = ['全部', '守护者', '复活社', '守护甜心'] as const;

const factionColor: Record<string, string> = {
  守护者: 'bg-secondary-100 text-secondary-900',
  复活社: 'bg-accent-100 text-accent-900',
  守护甜心: 'bg-primary-100 text-primary-700',
};

const siteUrl = 'https://starvoice3.github.io';

export default function CharacterList() {
  const [active, setActive] = useState<string>('全部');
  const [keyword, setKeyword] = useState('');

  const list = useMemo(() => {
    return characters.filter((c) => {
      const matchFaction = active === '全部' || c.faction === active;
      const kw = keyword.trim();
      const matchKw =
        !kw ||
        c.name.includes(kw) ||
        c.enName.toLowerCase().includes(kw.toLowerCase()) ||
        c.cv.includes(kw) ||
        c.title.includes(kw);
      return matchFaction && matchKw;
    });
  }, [active, keyword]);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '守护甜心动漫角色大全',
      url: `${siteUrl}/characters`,
      description: '守护甜心动漫主要角色资料大全，含背景故事与配音演员信息。',
      inLanguage: 'zh-CN',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: characters.map((c, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: c.name,
          url: `${siteUrl}/characters/${c.id}`,
        })),
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '角色大全', item: `${siteUrl}/characters` },
      ],
    },
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <JsonLd data={schema} />
      <nav aria-label="面包屑" className="mb-6">
        <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <li>
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
          </li>
          <li aria-hidden="true">
            <i className="ri-arrow-right-s-line" />
          </li>
          <li aria-current="page" className="font-medium text-foreground-800">
            角色大全
          </li>
        </ol>
      </nav>

      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="font-heading text-2xl font-black text-foreground-900 md:text-3xl">
            守护甜心动漫角色大全
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-foreground-600">
            圣夜学园守护者、复活社成员与守护甜心，主要角色资料一次集齐。点开任一角色查看完整背景故事与配音演员。
          </p>
        </div>
        <div className="relative w-full md:w-72">
          <i
            className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"
            aria-hidden="true"
          ></i>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索角色名 / 声优"
            aria-label="搜索角色"
            className="w-full rounded-full border border-background-300 bg-background-50 py-2.5 pl-11 pr-4 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="按阵营筛选角色">
        {factions.map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={active === f}
            onClick={() => setActive(f)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              active === f
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="mt-12 flex flex-col items-center gap-3 rounded-2xl border border-background-200 bg-background-100 py-16 text-center">
          <i className="ri-emotion-sad-line text-4xl text-foreground-400" aria-hidden="true"></i>
          <p className="text-sm text-foreground-600">没有找到匹配的角色，换个关键词试试吧。</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((c) => (
            <Link
              key={c.id}
              to={`/characters/${c.id}`}
              className="group flex flex-col overflow-hidden rounded-2xl border border-background-200/80 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-300"
            >
              <div className="relative h-64 w-full overflow-hidden bg-background-100">
                <img
                  src={c.image}
                  alt={`守护甜心动漫角色 ${c.name} 资料插画`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <span
                  className={`absolute left-3 top-3 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${factionColor[c.faction]}`}
                >
                  {c.faction}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg font-bold text-foreground-900">{c.name}</h2>
                  <span className="text-xs text-foreground-400">{c.enName}</span>
                </div>
                <p className="mt-1 text-sm font-medium text-primary-600">{c.title}</p>
                <p className="mt-3 flex-1 text-sm leading-6 text-foreground-600">{c.short}</p>
                <div className="mt-4 flex items-center justify-between border-t border-background-200/80 pt-3 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-mic-line text-primary-400" aria-hidden="true"></i>
                    配音：{c.cv}
                  </span>
                  <span className="inline-flex items-center gap-1 font-semibold text-primary-600">
                    查看详情
                    <i className="ri-arrow-right-s-line transition-transform group-hover:translate-x-0.5" aria-hidden="true"></i>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}